import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaSave, FaSpinner, FaCalendarAlt, FaBolt, FaFire, FaCar, FaTruck, FaSnowflake, FaTint, FaTrashAlt, FaShoppingCart, FaBuilding, FaPlane, FaIndustry, FaArrowRight, } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import Api1Form from '../../../../component/api/Api1Form';
import Api2Form from '../../../../component/api/Api2Form';
import Api3Form from '../../../../component/api/Api3Form';
import Api4Form from '../../../../component/api/Api4Form';
import Api5Form from '../../../../component/api/Api5Form';
import Api6Form from '../../../../component/api/Api6Form';
import Api7Form from '../../../../component/api/Api7Form';
import Api8Form from '../../../../component/api/Api8Form';
import Api9Form from '../../../../component/api/Api9Form';
import Api10Form from '../../../../component/api/Api10Form';
import Api11Form from '../../../../component/api/Api11Form';
import Api12Form from '../../../../component/api/Api12Form';

const UnifiedApiComponent = ({ apiNumbers: propApiNumbers = [] }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get apiNumbers from prop or URL params
  const urlApiNumbers = searchParams.get('apis');
  const apiNumbers = propApiNumbers.length > 0
    ? propApiNumbers
    : (urlApiNumbers ? urlApiNumbers.split(',').map(n => parseInt(n.trim())) : []);

  const [currentStep, setCurrentStep] = useState(0);
  const [allAnswers, setAllAnswers] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get questionId, userQuestionsAssignmentId, and subQuestionIds from URL params
  const questionId = searchParams.get('questionId');
  const userQuestionsAssignmentId = searchParams.get('userQuestionsAssignmentId');
  const returnUrl = searchParams.get('returnUrl') || '/questions-answers';
  const subQuestionIdsParam = searchParams.get('subQuestionIds');
  const subQuestionIds = subQuestionIdsParam
    ? subQuestionIdsParam.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
    : [];

  const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

  // API configurations
  const apiConfigs = {
    1: { name: 'Organization & Reporting Context', icon: FaCalendarAlt, color: 'blue' },
    2: { name: 'Grid Electricity Consumption', icon: FaBolt, color: 'yellow' },
    3: { name: 'Stationary Fuels', icon: FaFire, color: 'red' },
    4: { name: 'Mobile Fuels', icon: FaCar, color: 'orange' },
    5: { name: 'Logistics & Distribution', icon: FaTruck, color: 'purple' },
    6: { name: 'Refrigerants & Process Gases', icon: FaSnowflake, color: 'cyan' },
    7: { name: 'Water & Wastewater', icon: FaTint, color: 'blue' },
    8: { name: 'Waste Management', icon: FaTrashAlt, color: 'gray' },
    9: { name: 'Purchased Goods & Services', icon: FaShoppingCart, color: 'green' },
    10: { name: 'Capital Goods', icon: FaBuilding, color: 'indigo' },
    11: { name: 'Business Travel & Commuting', icon: FaPlane, color: 'pink' },
    12: { name: 'Process Emissions', icon: FaIndustry, color: 'brown' }
  };

  // Get current API number
  const currentApiNumber = apiNumbers[currentStep];
  const currentConfig = apiConfigs[currentApiNumber];

  // Initialize state for current API
  const initializeApiState = (apiNum) => {
    if (!allAnswers[apiNum]) {
      const baseState = {
        year: new Date().getFullYear().toString(),
        isCalculating: false,
        isSearching: false,
        calculationResults: {},
        availableFactors: {}
      };

      switch (apiNum) {
        case 1:
          return {
            ...baseState,
            formData: {
              reportingPeriodStart: '',
              reportingPeriodEnd: '',
              organizationalBoundary: [],
              countriesRegions: ['Chile'],
              marketBasedElectricity: false,
              dataQualityPreference: 'activity'
            }
          };
        case 2:
          return {
            ...baseState,
            formData: {
              inputMethod: '',
              unit: 'kWh',
              monthlyData: [],
              uploadedFile: null,
              supplierSpecific: false
            }
          };
        case 3:
          return {
            ...baseState,
            formData: {
              region: 'US',
              fuels: {
                naturalGas: { selected: false, spend: '', quantity: '', unit: 'm3' },
                diesel: { selected: false, spend: '', quantity: '', unit: 'l' },
                heavyFuelOil: { selected: false, spend: '', quantity: '', unit: 'l' },
                lpg: { selected: false, spend: '', quantity: '', unit: 'kg' },
                coal: { selected: false, spend: '', quantity: '', unit: 't' }
              }
            }
          };
        case 4:
          return {
            ...baseState,
            formData: {
              region: 'US',
              diesel: { selected: false, quantity: '', annualSpend: '', pricePerLiter: '' },
              gasoline: { selected: false, quantity: '', annualSpend: '', pricePerLiter: '' }
            }
          };
        case 5:
          return {
            ...baseState,
            formData: {
              shipsFreight: '',
              inputMethod: ''
            },
            activityEntries: [],
            spendEntries: [],
            unitsEntries: []
          };
        case 6:
          return {
            ...baseState,
            entries: []
          };
        case 7:
          return {
            ...baseState,
            formData: {
              recyclesWater: ''
            },
            waterEntries: [],
            recyclingEntries: []
          };
        case 8:
          return {
            ...baseState,
            formData: {
              finalConfirmation: '',
              missingInfo: ''
            },
            wasteEntries: [],
            inputMethod: 'quantity'
          };
        case 9:
          return {
            ...baseState,
            entries: [],
            inputMethod: 'activity'
          };
        case 10:
          return {
            ...baseState,
            entries: [],
            inputMethod: 'materials'
          };
        case 11:
          return {
            ...baseState,
            travelEntries: [],
            commutingData: {
              headcount: '',
              daysPerWeek: '',
              weeksPerYear: '',
              modes: [
                { mode: 'car_solo', percentage: '', distance: '' },
                { mode: 'carpool', percentage: '', distance: '', occupancy: '2.2' },
                { mode: 'bus', percentage: '', distance: '' },
                { mode: 'metro', percentage: '', distance: '' },
                { mode: 'rail', percentage: '', distance: '' },
                { mode: 'motorcycle', percentage: '', distance: '' },
                { mode: 'bike', percentage: '', distance: '' },
                { mode: 'walk', percentage: '', distance: '' },
                { mode: 'shuttle', percentage: '', distance: '' },
                { mode: 'telework', percentage: '', distance: '' }
              ],
              carFuelType: 'unknown'
            }
          };
        case 12:
          return {
            ...baseState,
            entries: [],
            inputMethod: 'throughput'
          };
        default:
          return baseState;
      }
    }
    return allAnswers[apiNum];
  };

  // Update answer state
  const updateApiState = (apiNum, updates) => {
    setAllAnswers(prev => ({
      ...prev,
      [apiNum]: {
        ...prev[apiNum],
        ...updates
      }
    }));
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < apiNumbers.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowSummary(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setShowSummary(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Get current API state
  const currentState = initializeApiState(currentApiNumber);
  const apiState = allAnswers[currentApiNumber] || currentState;

  // Render API forms based on number
  const renderApiForm = () => {
    switch (currentApiNumber) {
      case 1:
        return <Api1Form state={apiState} updateState={(updates) => updateApiState(1, updates)} />;
      case 2:
        return <Api2Form state={apiState} updateState={(updates) => updateApiState(2, updates)} />;
      case 3:
        return <Api3Form state={apiState} updateState={(updates) => updateApiState(3, updates)} />;
      case 4:
        return <Api4Form state={apiState} updateState={(updates) => updateApiState(4, updates)} />;
      case 5:
        return <Api5Form state={apiState} updateState={(updates) => updateApiState(5, updates)} />;
      case 6:
        return <Api6Form state={apiState} updateState={(updates) => updateApiState(6, updates)} />;
      case 7:
        return <Api7Form state={apiState} updateState={(updates) => updateApiState(7, updates)} />;
      case 8:
        return <Api8Form state={apiState} updateState={(updates) => updateApiState(8, updates)} />;
      case 9:
        return <Api9Form state={apiState} updateState={(updates) => updateApiState(9, updates)} />;
      case 10:
        return <Api10Form state={apiState} updateState={(updates) => updateApiState(10, updates)} />;
      case 11:
        return <Api11Form state={apiState} updateState={(updates) => updateApiState(11, updates)} />;
      case 12:
        return <Api12Form state={apiState} updateState={(updates) => updateApiState(12, updates)} />;
      default:
        return <div>Unknown API: {currentApiNumber}</div>;
    }
  };

  // Extract main value (total co2e) from calculation results
  const getMainValue = (state) => {
    if (!state || !state.calculationResults) return null;

    const results = state.calculationResults;
    const values = Object.values(results);

    if (values.length === 0) return null;

    // Sum all co2e values from the results
    let totalCo2e = 0;
    let unit = 'kg CO₂e'; // default unit

    values.forEach(result => {
      if (result && typeof result.co2e === 'number') {
        totalCo2e += result.co2e;
        // Use the unit from the first result (they should all be the same)
        if (!unit || unit === 'kg CO₂e') {
          unit = result.co2e_unit || 'kg CO₂e';
        }
      }
    });

    // Convert to tons if needed (if unit is in kg and value is large)
    if (unit === 'kg CO₂e' && totalCo2e >= 1000) {
      totalCo2e = totalCo2e / 1000;
      unit = 't CO₂e';
    }

    return { value: totalCo2e, unit };
  };

  // Calculate total sum of all API values
  const calculateTotalSum = () => {
    let total = 0;
    let unit = 'kg CO₂e';

    apiNumbers.forEach(apiNum => {
      const state = allAnswers[apiNum];
      const mainValue = getMainValue(state);
      if (mainValue) {
        // Convert to kg for consistent summing
        let valueInKg = mainValue.value;
        if (mainValue.unit === 't CO₂e') {
          valueInKg = valueInKg * 1000;
        }
        total += valueInKg;
      }
    });

    // Convert to tons if total is large
    if (total >= 1000) {
      total = total / 1000;
      unit = 't CO₂e';
    }

    return { value: total, unit };
  };

  // Handle save answer
  const handleSaveAnswer = async () => {
    if (!userQuestionsAssignmentId) {
      toastr.error('Question assignment information not available');
      return;
    }

    const totalSum = calculateTotalSum();
    
    // Validate that we have a valid total sum
    if (!totalSum || totalSum.value === undefined || isNaN(totalSum.value)) {
      toastr.error('Please complete at least one API calculation before saving');
      return;
    }
    
    // Save only the numeric value (without unit)
    const answerValue = totalSum.value.toFixed(2);

    setSaving(true);
    try {
      // If subquestion IDs are provided, save to subquestions instead of main question
      if (subQuestionIds.length > 0) {
        // Save to all subquestions (or just the first one if multiple)
        // Typically, the total sum should go to the first subquestion that asks for the total value
        const subQuestionIdToSave = subQuestionIds[0]; // Save to first subquestion

        console.log('[UnifiedApiComponent] Saving to subquestion:', {
          subQuestionId: subQuestionIdToSave,
          userQuestionsAssignmentId: parseInt(userQuestionsAssignmentId),
          answer: answerValue
        });

        const response = await axios.post("/subquestions/submit-subquestion-answer", {
          subQuestionId: subQuestionIdToSave,
          userQuestionsAssignmentId: parseInt(userQuestionsAssignmentId),
          answer: answerValue
        });

        console.log('[UnifiedApiComponent] Save response:', response.data);
        console.log('[UnifiedApiComponent] Response status:', response.status);
        console.log('[UnifiedApiComponent] Response data:', JSON.stringify(response.data, null, 2));

        if (response.data && response.data.status) {
          toastr.success("Answer saved to subquestion successfully");
          console.log('[UnifiedApiComponent] Answer saved successfully, navigating to:', returnUrl);
          // Navigate back after a short delay
          setTimeout(() => {
            navigate(returnUrl);
          }, 1500);
        } else {
          const errorMsg = response.data?.message || response.data?.error || 'Failed to save subquestion answer';
          console.error('[UnifiedApiComponent] Save failed:', errorMsg);
          toastr.error(errorMsg);
          setSaving(false); // Don't navigate if save failed
        }
      } else {
        // Fallback: Save to main question if no subquestion IDs provided
        if (!questionId) {
          toastr.error('Question information not available');
          return;
        }

        const response = await axios.post("/submit-answer", {
          userQuestionsAssignmentId: parseInt(userQuestionsAssignmentId),
          questionId: parseInt(questionId),
          answer: answerValue,
          answerType: "true",
          noAnswerReason: null,
        });

        if (response.data.status) {
          toastr.success("Answer saved successfully");
          // Navigate back after a short delay
          setTimeout(() => {
            navigate(returnUrl);
          }, 1500);
        } else {
          toastr.error(response.data.message || 'Failed to save answer');
        }
      }
    } catch (error) {
      console.error("Error saving answer:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || "Failed to save answer";
      toastr.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Render summary of all answers
  const renderSummary = () => {
    const totalSum = calculateTotalSum();
    const apiValues = [];

    apiNumbers.forEach(apiNum => {
      const config = apiConfigs[apiNum];
      const state = allAnswers[apiNum];
      const mainValue = getMainValue(state);
      apiValues.push({ apiNum, config, mainValue });
    });

    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Summary of Calculations</h2>

            <div className="space-y-4 mb-8">
              {apiValues.map(({ apiNum, config, mainValue }) => {
                const Icon = config.icon;
                return (
                  <div key={apiNum} className="p-4 border-2 border-gray-200 rounded-lg bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Icon className={`w-6 h-6 text-${config.color}-600`} />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-700">
                            {config.name}
                          </h3>
                          <p className="text-sm text-gray-500">API {apiNum}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {mainValue ? (
                          <div>
                            <div className="text-2xl font-bold text-green-600">
                              {mainValue.value.toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-500">{mainValue.unit}</div>
                          </div>
                        ) : (
                          <div className="text-gray-400 italic">No calculation</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total Sum */}
            <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-1">Total Emissions</h3>
                  <p className="text-sm text-gray-600">Sum of all calculated values</p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-green-700">
                    {totalSum.value.toFixed(2)}
                  </div>
                  <div className="text-lg text-gray-600 font-medium">{totalSum.unit}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (apiNumbers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">No APIs specified. Please provide apiNumbers prop or 'apis' URL parameter.</p>
        </div>
      </div>
    );
  }

  if (showSummary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="bg-gradient-to-r from-blue-600 to-green-600 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowSummary(false)}
                  className="bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-all duration-200"
                >
                  <FaArrowLeft className="w-5 h-5 text-white" />
                </button>
                <div>
                  <h1 className="text-white text-2xl font-semibold">Summary of Calculations</h1>
                  <p className="text-blue-100 text-sm">
                    Review and save your calculated emissions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {renderSummary()}

        {/* Save Button Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex justify-between items-center">
              <button
                onClick={() => setShowSummary(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 flex items-center space-x-2"
              >
                <FaArrowLeft className="w-4 h-4" />
                <span>Back to Edit</span>
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={handleBack}
                  className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAnswer}
                  disabled={saving}
                  className="px-8 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <FaSpinner className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FaSave className="w-4 h-4" />
                      <span>Save Answer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="bg-white/20 backdrop-blur-sm rounded-xl p-3 hover:bg-white/30 transition-all duration-200"
              >
                <FaArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  {currentConfig && <currentConfig.icon className="w-6 h-6 text-white" />}
                </div>
                <div>
                  <h1 className="text-white text-2xl font-semibold">
                    API {currentApiNumber}: {currentConfig?.name || 'Unknown'}
                  </h1>
                  <p className="text-blue-100 text-sm">
                    Step {currentStep + 1} of {apiNumbers.length}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {apiNumbers.map((num, idx) => (
                <div
                  key={num}
                  className={`w-3 h-3 rounded-full ${idx === currentStep ? 'bg-white' : idx < currentStep ? 'bg-green-300' : 'bg-white/30'
                    }`}
                  title={`API ${num}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white/50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="flex items-center justify-between text-sm text-gray-600">
            {apiNumbers.map((num, idx) => (
              <div
                key={num}
                className={`flex-1 text-center ${idx === currentStep ? 'font-semibold text-blue-600' :
                  idx < currentStep ? 'text-green-600' : 'text-gray-400'
                  }`}
              >
                API {num}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / apiNumbers.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Form Content */}
      {renderApiForm()}

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaArrowLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex space-x-3">
              <button
                onClick={handleBack}
                className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2"
              >
                <span>{currentStep === apiNumbers.length - 1 ? 'View Summary' : 'Next'}</span>
                {currentStep < apiNumbers.length - 1 && <FaArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedApiComponent;